import { useMemo } from 'react';
import {
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { usePatchLoanInvestorProgramFieldsMutation } from '@/apis/extended/loanApplicationApi';
import type {
  LoanApplication,
  LoanInvestorProgramFields,
} from '@/types/LoanApplicationType';
import { parseLoanPatchError } from './loanSectionForm';
import { LoanSectionFormFooter } from './LoanSectionFormFooter';
import { useLoanSectionForm } from './useLoanSectionForm';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
};

const EXIT_STRATEGIES = [
  'Sell after rehab',
  'Refinance to long-term rental',
  'Refinance to agency loan',
  'Hold and rent',
  'Other',
] as const;

const emptyInvestor = (): LoanInvestorProgramFields => ({
  rehabBudget: null,
  holdPeriodMonths: null,
  exitStrategy: '',
  afterRepairValue: null,
});

export function InvestorProgramSection({ application, readOnly }: Props) {
  const [patch, { isLoading }] = usePatchLoanInvestorProgramFieldsMutation();
  const isBridge = application.productType === 'bridge';

  const initial = useMemo(
    () => ({
      ...emptyInvestor(),
      ...(application.investorProgramFields || {}),
    }),
    [application._id, application.investorProgramFields, application.updatedAt]
  );

  const {
    draft: local,
    setDraft: setLocal,
    isDirty,
    markSaved,
    resetDraft,
    readOnly: formReadOnly,
  } = useLoanSectionForm({
    sectionKey: 'investor-program',
    applicationId: application._id,
    applicationUpdatedAt: application.updatedAt,
    initialValue: initial,
    readOnly,
  });

  const saveSection = async () => {
    if (formReadOnly) return;
    try {
      await patch({
        id: application._id,
        investorProgramFields: local,
      }).unwrap();
      markSaved(local);
      toast.success('Saved program details');
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const disabled = formReadOnly || isLoading;

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isBridge
          ? 'Bridge hold period and exit plan for this file.'
          : 'Rehab budget, ARV, and exit strategy for this investor file.'}
      </Typography>
      <Grid container spacing={2}>
        {!isBridge && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Rehab budget"
              type="number"
              disabled={disabled}
              value={local.rehabBudget ?? ''}
              onChange={(e) =>
                setLocal((prev) => ({
                  ...prev,
                  rehabBudget:
                    e.target.value === '' ? null : Number(e.target.value),
                }))
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="After-repair value (ARV)"
            type="number"
            disabled={disabled}
            value={local.afterRepairValue ?? ''}
            onChange={(e) =>
              setLocal((prev) => ({
                ...prev,
                afterRepairValue:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
        </Grid>
        {isBridge && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Hold period (months)"
              type="number"
              disabled={disabled}
              value={local.holdPeriodMonths ?? ''}
              onChange={(e) =>
                setLocal((prev) => ({
                  ...prev,
                  holdPeriodMonths:
                    e.target.value === '' ? null : Number(e.target.value),
                }))
              }
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Exit strategy"
            disabled={disabled}
            value={local.exitStrategy || ''}
            onChange={(e) =>
              setLocal((prev) => ({ ...prev, exitStrategy: e.target.value }))
            }
          >
            {EXIT_STRATEGIES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
      <LoanSectionFormFooter
        readOnly={formReadOnly}
        isDirty={isDirty}
        isSaving={isLoading}
        saveLabel="Save program details"
        onSave={saveSection}
        onCancel={resetDraft}
      />
    </div>
  );
}
